/**
 * The two brand marks, as geometry.
 *
 * The drawings live here rather than as `.svg` files under `assets/brand/` for the same reason the
 * palette does: more than one consumer needs them, and they must not drift. A React component
 * inlines these with `var(--brand-*)` fills; an email template inlines the same shapes with literal
 * hex, because mail clients ignore custom properties. An `<img>` tag could serve neither — it
 * cannot inherit a colour, and an emailed image is blocked by default in most clients.
 *
 * Nothing here names a colour. Every shape carries a ROLE, and the consumer resolves roles against
 * the palette for whatever ground it is drawing on. That is what keeps the feather rule honest:
 * the count is in the geometry and the roles, so it cannot be recoloured away by accident.
 *
 * ⚠ THIS FILE IS THE SOURCE, AND THIS NOTE USED TO SAY THE OPPOSITE. It read "Source:
 * assets/brand/*.svg and the construction rules in docs/BRAND-BOARD.md", while the asset folder's
 * own README called those SVGs the source of truth — so the two documents each pointed at the other
 * and neither was maintained. The exported full count-bird drifted: it drew the SUPERSEDED tail,
 * with the last two feathers nine units from the group above instead of sixteen, which is the
 * spacing WITHIN a group. It had no third group, so it counted to six — the exact defect the
 * geometry below was rewritten to fix on 2026-08-28. Its five count feathers also still carried
 * `#6D7C8E`, the label grey that failed contrast and was replaced by `#626F7F`.
 *
 * Corrected 2026-09-12. `assets/brand/*.svg` is now GENERATED from this file by
 * `pnpm brand:assets`, and CI fails if the checked-in files differ. The construction rules in
 * `docs/BRAND-BOARD.md` remain the design authority; this is their implementation.
 */

/**
 * `count` is the five middle feathers, `body` the two outer ones and the bird itself, `signal` the
 * single top feather. `eye` is knocked out of the body and vanishes in the one-colour rendition.
 */
export type MarkRole = 'signal' | 'count' | 'body' | 'eye';

/** What is BEHIND the mark. Never which page it is on. */
export type MarkGround = 'light' | 'navy' | 'mono';

/**
 * WHICH TOKEN EACH ROLE RESOLVES TO, PER GROUND — token NAMES, not values.
 *
 * ⚠ THIS LIVES HERE BECAUSE IT HAD TWO COPIES. `<CountBird>` held it as `var(--brand-*)` strings
 * and the SVG emitter needed the same answers as literal hex, so the mapping was written out twice
 * in two forms — which is precisely the arrangement that let the exported logos drift from the
 * on-screen mark in the first place. One table, two resolutions: the component prefixes
 * `--brand-` and the emitter looks the name up in the palette.
 *
 * ⚠ ON NAVY THE FIVE COUNT FEATHERS ARE `onDeep3`, NOT `ink3`. The brand board names `ink3` for
 * both grounds; measured, it is 2.74:1 on navy — under the 3:1 a graphic needs — while `onDeep3`
 * is 4.85:1. On light, `ink3` at 4.58:1 is exactly right. Same role, resolved for its ground.
 *
 * `mono` is not in here. One flat colour has no roles to resolve: it inherits `currentColor`
 * throughout and knocks the eye out to nothing, which is what keeps the head reading as a head.
 * The count is deliberately abandoned there — faking it with tints would be worse than not trying.
 */
export const MARK_ROLE_TOKENS: Record<Exclude<MarkGround, 'mono'>, Record<MarkRole, string>> = {
  light: { signal: 'signal', count: 'ink3', body: 'ink', eye: 'chalk' },
  navy: { signal: 'signal', count: 'onDeep3', body: 'onDeep', eye: 'deep' },
};

export type MarkShape =
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number; width: number; role: MarkRole }
  | { kind: 'path'; d: string; fill?: MarkRole; stroke?: MarkRole; width?: number }
  | { kind: 'circle'; cx: number; cy: number; r: number; role: MarkRole };

/* -------------------------------------------------------------------------- */
/* The Count                                                                   */
/* -------------------------------------------------------------------------- */

/** Every tail feather springs from this point. */
const TAIL = { x: 86, y: 64 } as const;

/**
 * ONE, FIVE, TWO — and the GROUPING carries it, with colour reinforcing rather than doing the job
 * alone.
 *
 * THE COUNT USED TO READ 1–5. Measured on the original drawing: the gap between the first group and
 * the second was 25°, but between the second and third only 13.6°, against 7.4–9.2° within a group.
 * A 13.6° gap next to a 9.2° gap is not a boundary anybody sees. And the last two feathers take the
 * `body` role, which on navy is the same value as the bird's body directly beside them — 1.00:1. So
 * the third group was separated by neither space nor colour, and the mark counted to six.
 *
 * Now: 28° and 22° between groups against a flat 7° within one. All eight radiate from the same
 * point at the same radius, which the original did not — its feathers ran from 35 to 45, so the fan
 * was subtly lopsided as well.
 *
 * Do not regroup these, and do not recolour a group by screen, market, season or status.
 */
const FEATHER_TIPS: [number, number, MarkRole][] = [
  [105.7, 26.9, 'signal'],
  [120.8, 40.5, 'count'],
  [123.4, 44.9, 'count'],
  [125.5, 49.6, 'count'],
  [126.9, 54.6, 'count'],
  [127.8, 59.6, 'count'],
  [126.4, 75.6, 'body'],
  [124.7, 80.4, 'body'],
];

export const COUNT_BIRD = {
  viewBox: '0 0 140 110',
  /** Stated rather than parsed back out of the viewBox, so a consumer can size by width. */
  aspect: 140 / 110,
  shapes: [
    ...FEATHER_TIPS.map(
      ([x2, y2, role]): MarkShape => ({
        kind: 'line',
        x1: TAIL.x,
        y1: TAIL.y,
        x2,
        y2,
        width: 4.6,
        role,
      }),
    ),
    /* Body, head, beak. */
    {
      kind: 'path',
      d: 'M40 45 C32 59 36 76 52 82 C67 88 83 80 91 70 C85 58 74 48 60 44 C52 42 45 41 40 45 Z',
      fill: 'body',
    },
    { kind: 'circle', cx: 42, cy: 36, r: 12.5, role: 'body' },
    { kind: 'path', d: 'M31 32 L16 39 L31 45 Z', fill: 'body' },
    /* Crest. */
    { kind: 'path', d: 'M46 24 Q47 13 54 8', stroke: 'body', width: 3.4 },
    { kind: 'path', d: 'M53 26 Q59 18 68 15', stroke: 'body', width: 3.4 },
    /* Wing line, legs, perch. */
    { kind: 'path', d: 'M52 55 C61 53 73 58 82 67', stroke: 'body', width: 3.2 },
    { kind: 'path', d: 'M56 83 L56 95 M68 82 L68 95', stroke: 'body', width: 3.2 },
    { kind: 'path', d: 'M38 97 L100 97', stroke: 'body', width: 4 },
    /* Knocked out of the head last, so it sits on top. */
    { kind: 'circle', cx: 41, cy: 34, r: 2.9, role: 'eye' },
  ] satisfies MarkShape[],
} as const;

/**
 * A DIFFERENT DRAWING, NOT A SMALLER ONE.
 *
 * Rendered and checked: at 24px the full mark's tail collapses into a smudge, the two lighter
 * groups merge, and the crest wisps read as antennae. The count is unrecoverable at that size no
 * matter how it is drawn — so this variant ABANDONS the count on purpose and optimises for
 * recognition instead: three heavy feathers with the top one signal, no crest, no legs, no perch,
 * no wing line, a larger eye.
 *
 * Never scale the full mark below 40px, and never scale this one above 48px.
 */
export const COUNT_BIRD_SMALL = {
  viewBox: '-2 0 118 96',
  aspect: 118 / 96,
  shapes: [
    { kind: 'line', x1: 62, y1: 52, x2: 96, y2: 22, width: 10, role: 'signal' },
    { kind: 'line', x1: 62, y1: 52, x2: 99, y2: 42, width: 10, role: 'body' },
    { kind: 'line', x1: 62, y1: 52, x2: 92, y2: 60, width: 10, role: 'body' },
    {
      kind: 'path',
      d: 'M30 36 C19 50 24 72 44 76 C60 79 74 70 78 58 C72 46 58 36 44 33 C38 32 33 32 30 36 Z',
      fill: 'body',
    },
    { kind: 'circle', cx: 33, cy: 27, r: 15, role: 'body' },
    { kind: 'path', d: 'M20 22 L2 31 L20 40 Z', fill: 'body' },
    { kind: 'circle', cx: 31, cy: 24, r: 4, role: 'eye' },
  ] satisfies MarkShape[],
} as const;

/**
 * THE MIDDLE RENDITION, and the one the product actually needs most.
 *
 * The mark ships at exactly two sizes in practice: 54px in the public header and 48px in the app
 * rail. Both used to clear the 40px threshold, so both got the eight-feather drawing — and at that
 * size the five count feathers merge into one fan. The two places most people will ever see the
 * mark were the two places the count did not survive.
 *
 * So this is the full silhouette with the tail reduced to 1 / 3 / 2 in heavier strokes: the
 * GROUPING is kept and the exact count is conceded, which is the honest trade at this size. Gaps of
 * 28° and 30° against 10° within a group — even clearer than the full mark, because there is room.
 */
export const COUNT_BIRD_MEDIUM = {
  viewBox: COUNT_BIRD.viewBox,
  aspect: COUNT_BIRD.aspect,
  shapes: [
    ...([
      [105.7, 26.9, 'signal'],
      [120.8, 40.5, 'count'],
      [124.4, 46.9, 'count'],
      [126.8, 53.8, 'count'],
      [126.4, 75.6, 'body'],
      [123.7, 82.4, 'body'],
    ] as [number, number, MarkRole][]).map(
      ([x2, y2, role]): MarkShape => ({
        kind: 'line',
        x1: TAIL.x,
        y1: TAIL.y,
        x2,
        y2,
        /* Heavier than the full mark's 4.6: six feathers with room, rather than eight without. */
        width: 6.4,
        role,
      }),
    ),
    /* Everything below the tail is the full drawing, unchanged — this is the same bird. */
    ...COUNT_BIRD.shapes.filter((s) => s.kind !== 'line'),
  ] satisfies MarkShape[],
} as const;

/**
 * Below this the eight-feather tail merges and the middle rendition takes over. It was 40, which
 * put both shipping sizes on the wrong side of it.
 */
export const COUNT_BIRD_MIN_FULL_PX = 72;
/** Below this the middle rendition gives way to the three-feather drawing. */
export const COUNT_BIRD_MIN_MEDIUM_PX = 40;
/** Above this, the small drawing's heavy feathers look like a mistake rather than a mark. */
export const COUNT_BIRD_MAX_SMALL_PX = 48;

/* -------------------------------------------------------------------------- */
/* The Wall                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Four walls × nineteen stacks × two tiles high = 152. Every game begins by building the complete
 * set, so the pattern is a COUNT of the number rather than a picture of it.
 *
 * Construction is from the brand board: tiles 11 × 14 units, corner radius 1.5, horizontal pitch
 * 13, two courses with the top at y=0 and the bottom at y=16. Generated rather than stored as 152
 * rectangles, because the drawn tile's position is a variable and hand-editing a 10KB file to move
 * it is how a pattern ends up with two drawn tiles.
 *
 * NOTE FOR THE COMMISSIONER: the arithmetic holds only while leagues set up the standard way and
 * sets carry no extra blanks. It is stated as a fact on public pages and still needs one
 * confirmation — see DECISIONS.md.
 */
export const WALL_TILE = { width: 11, height: 14, radius: 1.5, pitch: 13 } as const;

/** The whole set, and one wall of it. 76 stacks × 2 = 152; 19 stacks × 2 = 38. */
export type WallLength = 152 | 19;

const STACKS: Record<WallLength, number> = { 152: 76, 19: 19 };

export interface WallTile {
  x: number;
  y: number;
  /** The one tile lifted out of the top course. Exactly one per wall, or none. */
  drawn: boolean;
}

/**
 * THE DRAWN TILE MEANS SOMETHING: one tile lifted five units out of the top course is a tile in
 * play. Use it only where something is genuinely in progress, never as static ornament, and never
 * more than one — its POSITION is the variable, not its colour.
 *
 * `drawnStack` is an index into the stacks; out-of-range or undefined draws a plain wall.
 */
export function wallTiles(length: WallLength, drawnStack?: number): WallTile[] {
  const stacks = STACKS[length];
  const tiles: WallTile[] = [];

  for (let stack = 0; stack < stacks; stack += 1) {
    const x = stack * WALL_TILE.pitch;
    const drawn = stack === drawnStack;

    /* Bottom course, then top — so the lifted tile paints over its neighbours' corners. */
    tiles.push({ x, y: 16, drawn: false });
    tiles.push({ x, y: drawn ? -5 : 0, drawn });
  }

  return tiles;
}

/** `0 -6 W 37` — the -6 leaves room above for the lifted tile. */
export function wallViewBox(length: WallLength): string {
  const stacks = STACKS[length];
  const width = (stacks - 1) * WALL_TILE.pitch + WALL_TILE.width;
  return `0 -6 ${width} 37`;
}

/* ---------------------------------------------------------------------------------------------
 * The counting wall
 * ------------------------------------------------------------------------------------------ */

/**
 * A WALL WHOSE TILE COUNT IS A REAL NUMBER, and one tile drawn per unit counted.
 *
 * `wallTiles` above is DECORATION: 152 tiles because that is how many are in a set, and exactly one
 * drawn tile meaning one thing in progress. This is the other kind. Six tiles because the league
 * runs six sessions, and three drawn because three of them have been played. The brand board carried
 * "never more than one drawn tile per wall" as an absolute; the commissioner scoped it to decorative
 * walls on 2026-08-30 so that this could exist. It is the only counting wall in the product, and a
 * second one needs a reason rather than a prop.
 *
 * Single course — no bottom row. A decorative wall is two tiles high because a real wall is; a
 * counting wall is a row of units and a second course would double every number on sight.
 *
 * COUNTED, NOT POSITIONAL: the drawn tiles are the first `drawn` of them, never a gap where an
 * unplayed session sits. On today's data that is automatic rather than a choice — the number counts
 * how far through the league everyone is, so the played sessions ARE the first n and no gap can
 * arise. It is written as a filter anyway, and tested, because the day this starts counting one
 * PLAYER's sessions is the day a gap becomes possible, and that day it is a ruling and not a bug:
 * a session she missed must not be re-announced on her own screen every week for the rest of the
 * league. Her absence is on the record in the standings table, which is where it belongs.
 */
export function countingWallTiles(total: number, drawn: number): WallTile[] {
  /*
    CLAMPED, NOT THROWN. A scoring edit can briefly produce more played sessions than scheduled ones,
    or a negative count mid-recalculation, and a mark is the wrong place to discover that. It draws
    something sane and the numbers reconcile a moment later.
  */
  const tiles = Math.max(0, Math.trunc(total));
  const lifted = Math.min(tiles, Math.max(0, Math.trunc(drawn)));

  const flat: WallTile[] = [];
  const raised: WallTile[] = [];

  for (let i = 0; i < tiles; i += 1) {
    const x = i * WALL_TILE.pitch;
    if (i < lifted) raised.push({ x, y: -5, drawn: true });
    else flat.push({ x, y: 0, drawn: false });
  }

  /* Drawn last, so a lifted tile paints over its neighbour's corner — same as `wallTiles`. */
  return [...flat, ...raised];
}

/** `0 -6 W 20` — the -6 leaves room above for the lifted course, and there is no bottom one. */
export function countingWallViewBox(total: number): string {
  const tiles = Math.max(1, Math.trunc(total));
  const width = (tiles - 1) * WALL_TILE.pitch + WALL_TILE.width;
  return `0 -6 ${width} 20`;
}

/**
 * ABOVE THIS THE TILES STOP READING AS TILES.
 *
 * Twelve tiles across a 320px phone panel is 23px a tile, which still shows the shape and the gap.
 * Beyond that the row becomes a striped smear that counts nothing, and a caller should fall back to
 * a plain bar rather than draw one. Leagues in this product run three to eight sessions, so the limit
 * is headroom, not a constraint anybody will meet.
 */
export const COUNTING_WALL_MAX_TILES = 12;

/**
 * HOW BIG A TILE IS DRAWN, and it is a fixed scale rather than a width that fills its container.
 *
 * The mockup the commissioner signed off draws the six-tile row at 304px inside a 320px phone panel
 * — four screen pixels per geometry unit, so a tile is 44 × 56. Fixing the SCALE and letting the ROW
 * LENGTH vary is the point: a longer league is a longer row, which is what "counting" means. Sizing
 * it to fill the container instead would make a four-session league's tiles enormous and an eight-
 * session league's small, and the row would stop being comparable to itself.
 *
 * The caller still caps it at the container width, so an eight-tile row on a narrow phone shrinks
 * proportionally rather than overflowing.
 */
export const COUNTING_WALL_UNIT_PX = 4;

/** The row's natural width in screen pixels, before any container cap. */
export function countingWallWidth(total: number): number {
  const tiles = Math.max(1, Math.trunc(total));
  return ((tiles - 1) * WALL_TILE.pitch + WALL_TILE.width) * COUNTING_WALL_UNIT_PX;
}

/** Below this the 152-tile wall compresses to an unreadable stripe; one wall is used instead. */
export const WALL_NARROW_BREAKPOINT_PX = 700;

/* -------------------------------------------------------------------------- */
/* The seat winds                                                              */
/* -------------------------------------------------------------------------- */

/**
 * THE FOUR WINDS, AS GLYPH OUTLINES.
 *
 * These are standardised characters, not illustrations, and the correct forms already exist in
 * every CJK typeface. They were previously set as live text in Libre Franklin — which contains no
 * CJK — so all of them fell through to whatever face the machine happened to have: Yu Gothic on one
 * Windows laptop, PingFang on a Mac. The seating board looked different on a director's screen than
 * on ours, and the print vendor's output would have depended on what was installed on their RIP.
 *
 * Outlined from Noto Sans CJK TC Black and Noto Serif CJK TC Bold, both OFL, both of which permit
 * outlining and commercial use. Drawing them by hand was considered and rejected: that is how you
 * end up with wrong stroke counts and wrong inner components in a character a player can read.
 *
 * TRADITIONAL FORMS, as used on real mahjong tiles. `東`, never the simplified `东`.
 *
 * FOUR, AND THERE IS NO FIFTH WIND. The fifth chair at a five-player table is not a wind and must
 * not borrow a character — `中` is the red dragon tile, and a seat card carrying it reads, to anyone
 * who knows the tiles, like a dragon was set down where a player should be. The fifth chair gets
 * `<RotateCard>`, which is deliberately a different class of object.
 *
 * Two things the extraction had to get right, kept here because both are silent when wrong:
 *   - A .ttc carries JP / KR / SC / TC / HK faces and THE DEFAULT INDEX IS JP. The face was chosen
 *     by nameID 6 containing "tc" — the collections spell it lowercase.
 *   - Font coordinates are Y-up and SVG is Y-down, hence the flip about the em box below. Without
 *     it every character is mirrored vertically, which for these four is subtle enough to ship.
 *
 * One em box of 1000 for all eight, so the four sit on a common baseline set side by side.
 */
export type SeatWind = 'east' | 'south' | 'west' | 'north';

/** The default is the sans: it holds up at tent-card distance, in one-colour print, and stitched. */
export type SeatWeight = 'sans' | 'serif';

export const SEAT_WIND_EM = 1000;

/** The flip from font space to SVG space. Applied by the component, stated once here. */
export const SEAT_WIND_TRANSFORM = `translate(0,${SEAT_WIND_EM}) scale(1,-1)`;

/** English names, because a screen reader must never announce a bare character to a player. */
export const SEAT_WIND_NAME: Record<SeatWind, string> = {
  east: 'East',
  south: 'South',
  west: 'West',
  north: 'North',
};

export const SEAT_WIND_PATH: Record<SeatWeight, Record<SeatWind, string>> = {
  sans: {
    east:
      'M136 602V208H317C238 136 130 74 22 36C54 7 100 -50 122 -86C232 -39 337 35 421 124V-95H573V130C659 39 767 -40 878 -88C901 -49 947 9 982 39C872 76 761 138 681 208H871V602H573V644H950V779H573V855H421V779H54V644H421V602ZM278 356H421V315H278ZM573 356H722V315H573ZM278 495H421V455H278ZM573 495H722V455H573Z',
    south:
      'M423 845V782H54V647H423V589H82V-92H228V456H393L312 433C328 404 346 365 356 336H281V225H428V179H260V64H428V-61H565V64H738V179H565V225H714V336H646C664 362 683 394 703 429L603 456H768V48C768 33 762 28 744 28C729 27 666 27 625 30C643 -2 665 -55 672 -91C752 -91 812 -90 857 -70C902 -50 918 -19 918 47V589H582V647H946V782H582V845ZM399 336 481 363C471 389 453 426 434 456H579C567 421 545 374 527 342L548 336Z',
    west:
      'M43 806V666H325V578H92V-91H233V-37H776V-91H924V578H675V666H953V806ZM233 96V223C249 205 263 186 271 173C409 232 447 341 453 445H538V367C538 234 562 193 679 193C703 193 748 193 774 193H776V96ZM233 298V445H324C319 390 301 339 233 298ZM454 578V666H538V578ZM675 445H776V330C772 329 767 328 759 328C748 328 712 328 703 328C678 328 675 331 675 368Z',
    north:
      'M13 71 78 -79C199 -33 352 28 493 87L467 218L422 203V840H270V627H51V482H270V151C171 119 79 89 13 71ZM533 839V138C533 -21 570 -70 694 -70C716 -70 783 -70 806 -70C928 -70 962 14 974 224C935 233 870 263 835 291C829 117 823 72 790 72C778 72 731 72 718 72C688 72 685 80 685 137V484H937V629H685V839Z',
  },
  serif: {
    east:
      'M436 306H292V418H436ZM557 306V418H703V306ZM172 587V215H190C239 215 292 241 292 253V277H380C308 156 181 29 30 -50L37 -63C197 -12 334 62 436 156V-89H461C507 -89 557 -64 557 -53V268C623 116 730 8 876 -57C890 1 926 39 971 51L973 62C827 93 665 170 576 277H703V230H724C766 230 825 256 826 264V539C846 543 859 551 866 559L749 648L693 587H557V681H920C935 681 946 686 949 697C901 740 822 801 822 801L752 710H557V805C584 809 591 820 594 834L436 849V710H53L61 681H436V587H300L172 636ZM436 447H292V558H436ZM557 447V558H703V447Z',
    south:
      'M325 498 316 493C340 458 364 402 364 354C450 280 553 448 325 498ZM596 838 441 851V704H40L49 676H441V544H250L121 596V-90H140C190 -90 241 -62 241 -48V515H773V56C773 43 768 35 751 35C725 35 622 43 621 43V28C673 21 695 7 712 -11C728 -28 733 -55 737 -92C874 -80 893 -34 893 44V496C914 500 927 509 934 516L818 605L763 544H560V676H934C949 676 961 681 964 692C915 733 836 791 836 791L767 704H560V810C587 814 594 824 596 838ZM656 388 607 330H550C591 367 633 414 661 448C683 447 695 455 699 466L566 504C556 453 538 382 522 330H284L292 302H441V181H262L270 153H441V-59H461C520 -59 554 -39 555 -34V153H727C741 153 751 158 754 169C716 202 655 248 655 248L601 181H555V302H720C734 302 744 307 746 318C711 348 656 388 656 388Z',
    west:
      'M549 524V297C549 228 561 205 641 205H697C735 205 764 207 786 211V42H216V524H338C337 392 324 258 217 153L226 143C424 240 448 391 449 524ZM549 552H449V729H549ZM786 314 768 311C762 310 751 309 745 309C738 309 724 309 711 309H677C661 309 658 313 658 328V524H786ZM848 844 779 757H32L40 729H338V552H227L102 600V-75H122C181 -75 216 -53 216 -45V14H786V-71H807C866 -71 906 -46 906 -40V513C929 518 940 525 947 534L840 619L782 552H658V729H947C962 729 973 734 976 745C928 785 848 844 848 844Z',
    north:
      'M711 812 559 828V66C559 -20 588 -43 687 -43H773C927 -43 973 -21 973 29C973 49 963 64 931 78L928 226H917C900 164 883 103 871 85C863 75 855 72 844 70C832 70 810 69 784 69H714C684 69 676 78 676 100V495H932C946 495 957 500 959 511C922 556 851 627 851 627L788 524H676V784C701 788 710 799 711 812ZM452 813 300 828V525H63L72 496H300V102C186 83 90 69 40 63L100 -86C112 -82 123 -73 128 -59C315 20 440 83 521 129L519 140L415 122V785C442 789 450 799 452 813Z',
  },
};
